import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { Card, TwoLineGrid } from '~components';
import OrganisationsService from '~features/organisations/services/organisationsService';
import { getService } from '~services/serviceFactory';
import { formatDateTime } from '~utils/dateUtils';
import { ifNullOrEmpty } from '~utils/stringUtils';

interface OrganisationInformationCardProps {
	organisationId: string;
}

const organisationsService = getService(OrganisationsService);

const OrganisationInformationCard = ({
	organisationId: organisationId,
}: OrganisationInformationCardProps) => {
	const { t } = useTranslation();

	const {
		data: organisationData,
		error: organisationError,
		isLoading: organisationIsLoading,
	} = useSWR(
		organisationsService.getOrganisationByIdCacheKey(organisationId),
		([_, id]) => (id ? organisationsService.getOrganisationById(id) : null),
		{
			revalidateOnMount: false,
		}
	);

	const isInitialLoading = organisationIsLoading && organisationData == undefined;
	const hasError = organisationError != undefined;

	const email = ifNullOrEmpty(organisationData?.emailAddress);
	const phone = ifNullOrEmpty(organisationData?.phoneNumber);

	return (
		<Card
			title={t('organisation.fields.generalInformation', { ns: 'entities' })}
			sx={{ height: '100%' }}
		>
			<TwoLineGrid
				isLoading={isInitialLoading}
				showError={hasError}
				twoLineItems={[
					{
						primary: t('general.name', { ns: 'entities' }),
						secondary: ifNullOrEmpty(organisationData?.name),
					},
					{
						primary: t('general.email', { ns: 'entities' }),
						secondary: email,
						linkTarget: '_self',
						link: organisationData?.emailAddress ? `mailto:${email}` : undefined,
					},
					{
						primary: t('general.phoneNumber', { ns: 'entities' }),
						secondary: phone,
						linkTarget: '_self',
						link: organisationData?.phoneNumber ? `tel:${phone}` : undefined,
					},
					{
						primary: t('created', { ns: 'date' }),
						secondary: formatDateTime(organisationData?.creationDate),
					},
				]}
			/>
		</Card>
	);
};

export default OrganisationInformationCard;
